import { css } from '@emotion/react';
import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { MaBankSelection } from '../../../gqltypes';
import { pageMap } from '../../../paths';
import { setBankSelection } from '../../../services';
import { useActions, useSelector } from '../../../state';
import { selectors } from '../../../state/editor';
import { getOnboardingUrl } from '../../../utils';
import { ButtonLink, TextButton } from '../../buttons';
import { StepContainer } from '../Container';
import { StepHeading } from '../step-heading';

export const ChooseBankStep: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const userId = searchParams.get('bruker');
  const { bankSelection, id } = useSelector(e => ({
    id: e.founding.id,
    bankSelection: e.founding.bankSelection,
  }));
  const { canOnboard } = useSelector(e => ({
    canOnboard: selectors.userCanOnboard(e, userId ?? e.editor.formOwner),
  }));
  const onboardingUrl = getOnboardingUrl(id, bankSelection);
  const { foundingActions } = useActions();

  const applyBankSelection = async (selection: MaBankSelection) => {
    await setBankSelection({ id, selection });
    foundingActions.updateFounding({
      bankSelection: selection,
    });

    // This will show another screen, but not change the URL
    // so we have to scroll manually.
    window.scrollTo(0, 0);
  };

  return (
    <StepContainer>
      <StepHeading>Åpne konto</StepHeading>

      {canOnboard ? (
        <>
          <p>
            For å bli registrert må selskapet ha en bedrifts&shy;konto, som du
            skal sette aksje&shy;kapitalen på.
          </p>

          <p>
            <b>Bankkonto oppretter du enkelt med Folio</b>
            <br />
            Dette er en bedrifts&shy;konto som er fri for små&shy;gebyrer,
            gratis første måneden, og uten bindings&shy;tid.{' '}
            <Link
              to={{ pathname: pageMap.findOutMore, search: location.search }}
            >
              Les om bedriftskonto fra Folio
            </Link>
            .
          </p>
          <p>
            Senere i prosessen kan du sette opp regnskap med Fiken. Dersom du
            velger å opprette konto hos både Fiken og Folio blir det automatisk
            opprettet en integrasjon mellom bank og regnskap for deg.
          </p>

          <div
            css={css`
              text-align: center;
            `}
          >
            <p>
              <ButtonLink href={onboardingUrl} id="to-onboarding">
                Åpne Folio bedriftskonto
              </ButtonLink>
            </p>
            <TextButton onClick={() => applyBankSelection('OTHER')}>
              Opprett konto i en annen bank
            </TextButton>
          </div>
        </>
      ) : (
        <>
          <p>
            Før bedriften kan registreres, må daglig leder eller styreleder
            bestille konto til selskapet. Vi har sendt lenke på e-post til rett
            person.
          </p>

          <p>
            <b>Bankkonto oppretter du enkelt med Folio</b>
            <br />
            Dette er en bedrifts&shy;konto som er fri for små&shy;gebyrer,
            gratis første måneden, og uten bindings&shy;tid.{' '}
            <Link
              to={{ pathname: pageMap.findOutMore, search: location.search }}
            >
              Les om bedriftskonto fra Folio
            </Link>
            .
          </p>
          <p>
            Senere i prosessen kan du sette opp regnskap med Fiken. Dersom du
            velger å opprette konto hos både Fiken og Folio blir det automatisk
            opprettet en integrasjon mellom bank og regnskap for deg.
          </p>
        </>
      )}
    </StepContainer>
  );
};
